import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import OptimizedImage from '../../shared/OptimizedImage';
import ButtonLink from '../../shared/ButtonLink';
import { ImageWithContentTile } from './types/ImageWithContentTile';

export default function ImageWithContent({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: ImageWithContentTile;
  trackingPrefix?: string;
}>) {
  const fullWidth = tile['full-width']?.value;
  const isNotOnTop = tile['is-not-on-top']?.value;
  const contentPosition = tile['content-position'].options?.[0].value;
  let contentPositionClasses;
  let buttonClasses;
  let gradient;
  const { title, subtitle, icons } = tile;

  switch (contentPosition) {
    case 'center-center': {
      contentPositionClasses = 'justify-center items-center text-center';
      buttonClasses = 'mx-auto';
      gradient = 'bg-gradient-middle';
      break;
    }
    case 'center-top': {
      contentPositionClasses = 'justify-center items-start text-center';
      buttonClasses = 'mx-auto';
      gradient = 'bg-gradient-top';
      break;
    }
    case 'center-bottom': {
      contentPositionClasses = 'justify-center items-end text-center';
      buttonClasses = 'mx-auto';
      gradient = 'bg-gradient-bottom';
      break;
    }
    case 'left-top': {
      contentPositionClasses = 'justify-start items-start';
      gradient = 'bg-gradient-top';
      break;
    }
    case 'left-center': {
      contentPositionClasses = 'justify-start items-center';
      gradient = 'bg-gradient-middle';
      break;
    }
    case 'left-bottom': {
      contentPositionClasses = 'justify-start items-end';
      gradient = 'bg-gradient-bottom';
      break;
    }
    case 'right-top': {
      contentPositionClasses = 'justify-end items-start text-right';
      buttonClasses = 'ml-auto';
      gradient = 'bg-gradient-top';
      break;
    }
    case 'right-center': {
      contentPositionClasses = 'justify-end items-center text-right';
      buttonClasses = 'ml-auto';
      gradient = 'bg-gradient-middle';
      break;
    }
    case 'right-bottom': {
      contentPositionClasses = 'justify-end items-end text-right';
      buttonClasses = 'ml-auto';
      gradient = 'bg-gradient-bottom';
      break;
    }
    default: {
      contentPositionClasses = 'justify-center items-center';
      buttonClasses = 'mx-auto';
      gradient = 'bg-gradient-middle';
      break;
    }
  }

  return (
    <div
      className={twJoin(
        !fullWidth
          ? 'mx-auto w-full max-w-screen-2xl px-4 sm:px-8'
          : !isNotOnTop
            ? '-mt-[104px] pb-20'
            : 'my-8 sm:my-14 lg:my-16',
      )}
    >
      <div
        className={twMerge(
          'relative flex p-5 sm:p-8',
          fullWidth ? 'h-screen' : 'h-sm sm:h-md lg:h-lg',
          contentPositionClasses,
          !fullWidth ? 'my-6 overflow-hidden rounded-xl sm:my-10' : 'mt-0',
        )}
      >
        <OptimizedImage
          imageComponent={tile['background-image']}
          fill
          className="absolute inset-0 -z-10 h-full w-full object-cover"
          sizes="100vw"
        />
        {/* Gradient overlay */}
        <div className={twJoin('absolute inset-0 -z-10', gradient)} />
        <div className="max-w-lg text-white">
          <RichTextRenderer
            blocks={title}
            className="mb-2 text-3xl sm:mb-4 md:text-4xl lg:text-5xl"
          />
          <RichTextRenderer blocks={subtitle} className="text-md md:text-lg lg:text-2xl" />
          {icons?.images?.length && icons.images.length > 0 && (
            <div className="mx-auto flex w-fit gap-4 sm:gap-6 md:gap-8">
              {icons.images?.map((icon) => (
                <OptimizedImage
                  key={icon.url}
                  image={icon}
                  width={100}
                  height={50}
                  sizes="100px"
                  className="h-10 w-auto"
                />
              ))}
            </div>
          )}
          <ButtonLink
            ctaComponent={tile.cta}
            className={twJoin(buttonClasses, 'mt-4 w-fit sm:mt-6')}
            trackingPrefix={trackingPrefix}
          />
        </div>
      </div>
    </div>
  );
}
